import {
	CheckOutlined as CheckIcon,
	DescriptionOutlined,
	EditOutlined,
	LockOutlined,
	Whatshot,
	HomeOutlined as HomeIcon,
	BoltOutlined,
} from "@mui/icons-material";
import { Box, FormControlLabel, Grid, IconButton, Paper, Stack, Switch, Typography } from "@mui/material";
import React, { useMemo, useState, useEffect } from "react";
import { VMaskTextField } from "../../../../shared/forms/VMaskTextField";
import { globalRemoveMaskReal2 } from "../../../../shared/utils";
import { NormalizedCoverage } from "../utils/normalizeCoverage";

export interface CoverageCardProps {
	coverages: NormalizedCoverage[];
	rentValue: string;
	onChange?: (updatedCoverages: NormalizedCoverage[]) => void;
}

export const CoverageCardQuote: React.FC<CoverageCardProps> = ({ coverages = [], rentValue, onChange }) => {
	const [editingKey, setEditingKey] = useState<string | null>(null);
	const [tempValue, setTempValue] = useState("");
	const [localCoverages, setLocalCoverages] = useState<NormalizedCoverage[]>([]);

	// 🔹 Normaliza enabled + garante que cobertura obrigatória sempre esteja ativa
	useEffect(() => {
		const updated = coverages.map((c) => {
			const name = (c.name || "").toLowerCase();

			// Apenas INCÊNDIO obrigatório
			const isMandatory = name.includes("incênd") || name.includes("incend");

			return {
				...c,
				isMandatory,
				enabled: isMandatory ? true : (c as any).enabled ?? true,
			};
		});

		setLocalCoverages(updated);
	}, [coverages]);


	// 🔹 Calcula ícones + valor formatado com base no novo modelo
	const computedCoverages = useMemo(() => {
		return localCoverages
			.map((c, idx) => {
				let icon: JSX.Element = <DescriptionOutlined color="action" />;

				const nameLower = (c.name || "").toLowerCase();
				const isIncendio = nameLower.includes("incênd") || nameLower.includes("incend");

				if (isIncendio) icon = <Whatshot />;
				else if (nameLower.includes("aluguel")) icon = <HomeIcon />;
				else if (nameLower.includes("elétric") || nameLower.includes("eletric")) icon = <BoltOutlined />;

				return {
					...c,
					id: c.id ?? `${idx}`,
					isMandatory: isIncendio,
					icon,
					enabled: isIncendio ? true : c.enabled ?? true,

					valueFormatted:
						c.coverage_value !== undefined
							? Number(c.coverage_value).toLocaleString("pt-BR", {
								style: "currency",
								currency: "BRL",
							})
							: "R$ 0,00",
				};
			})
			.sort((a, b) => {
				const aName = a.name.toLowerCase();
				const bName = b.name.toLowerCase();

				const aIsIncendio = aName.includes("incênd") || aName.includes("incend");
				const bIsIncendio = bName.includes("incênd") || bName.includes("incend");

				// 1) Incêndio sempre primeiro
				if (aIsIncendio && !bIsIncendio) return -1;
				if (!aIsIncendio && bIsIncendio) return 1;

				// 2) Depois todos os enabled (ativos)
				if (a.enabled && !b.enabled) return -1;
				if (!a.enabled && b.enabled) return 1;

				// 3) Mantém ordem original (não reordena nada além disso)
				return 0;
			});

	}, [localCoverages]);



	// 🟦 Inicia edição com valor numérico
	const handleEditClick = (coverageId: string) => {
		const item = localCoverages.find((c) => c.id === coverageId);
		if (!item) return;

		const numeric = Number(item.coverage_value ?? item.limite_min ?? 0);

		const formatted =
			!isNaN(numeric) && numeric > 0
				? numeric.toLocaleString("pt-BR", {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				})
				: "";

		setTempValue(formatted);
		setEditingKey(coverageId);
	};

	// 🟩 Valida min/max
	const handleValidateValue = (coverage: NormalizedCoverage) => {
		const numericValue = Number(globalRemoveMaskReal2(tempValue));
		if (isNaN(numericValue)) return;

		const min = coverage.limite_min ?? 0;
		const max = coverage.limite_max ?? 0;

		let adjusted = numericValue;
		if (numericValue < min) adjusted = min;
		if (max > 0 && numericValue > max) adjusted = max;

		setTempValue(
			adjusted.toLocaleString("pt-BR", {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			})
		);
	};

	// ✅ Salva valor editado no modelo NormalizedCoverage
	const handleSaveClick = (coverageId: string) => {
		const numeric = Number(globalRemoveMaskReal2(tempValue));

		setLocalCoverages((prev) => {
			const updated = prev.map((c) =>
				c.id === coverageId
					? {
						...c,
						coverage_value: numeric,
					}
					: c
			);

			onChange?.(updated);
			return updated;
		});

		setEditingKey(null);
		setTempValue("");
	};

	// 🔀 Toggle de cobertura (não muda valores)
	const handleToggleCoverage = (coverageId: string, enabled: boolean) => {
		setLocalCoverages((prev) => {
			const updated = prev.map((c) =>
				c.id === coverageId
					? {
						...c,
						enabled: c.coverageId === "1" ? true : enabled,
					}
					: c
			);

			onChange?.(updated);
			return updated;
		});
	};

	return (
		<Paper sx={{ p: 1, mb: 3 }} variant="outlined">
			<Stack direction="row" alignItems="center" mb={2}>
				<Whatshot color="primary" sx={{ mr: 1 }} />
				<Typography variant="h6">Coberturas</Typography>
			</Stack>

			{computedCoverages.length === 0 ? (
				<Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
					Nenhuma cobertura disponível.
				</Typography>
			) : (
				<Grid container spacing={2} direction="column">
					{computedCoverages.map((coverage) => {
						const { id } = coverage;

						const numericTemp = Number(globalRemoveMaskReal2(tempValue));
						const min = coverage.limite_min ?? 0;
						const max = coverage.limite_max ?? 0;

						const isOutOfRange = editingKey === id && ((min > 0 && numericTemp < min) || (max > 0 && numericTemp > max));

						return (
							<Grid item key={id}>
								<Paper
									variant="outlined"
									sx={{
										p: 1,
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										borderRadius: 2,
										opacity: coverage.enabled ? 1 : 0.5,
									}}
								>
									<FormControlLabel
										control={
											<Switch
												checked={coverage.enabled}
												onChange={(e) => handleToggleCoverage(id, e.target.checked)}
												disabled={coverage.isMandatory}
											/>
										}
										label={
											<Stack direction="row" alignItems="center" spacing={1}>
												{coverage.icon}
												<Typography>{coverage.name}</Typography>
											</Stack>
										}
									/>

									{/* Coluna direita */}
									{coverage.enabled && (
										<Stack direction="row" alignItems="center" spacing={1}>
											{editingKey === id ? (
												<Box sx={{ width: 220 }}>
													<VMaskTextField
														fullWidth
														size="small"
														mask="real"
														name={`coverage_value_${id}`}
														value={tempValue}
														onChange={(e) => setTempValue(e.target.value)}
														onBlur={() => handleValidateValue(coverage)}
													/>

													{(coverage.limite_min || coverage.limite_max) && (
														<Typography
															variant="caption"
															sx={{
																pl: 0.5,
																color: isOutOfRange ? "error.main" : "text.secondary",
															}}
														>
															Mín:{" "}
															{coverage.limite_min?.toLocaleString("pt-BR", {
																style: "currency",
																currency: "BRL",
															})}{" "}
															| Máx:{" "}
															{coverage.limite_max?.toLocaleString("pt-BR", {
																style: "currency",
																currency: "BRL",
															})}
														</Typography>
													)}
												</Box>
											) : (
												<Typography variant="body1">{coverage.valueFormatted}</Typography>
											)}

											{editingKey === id ? (
												<IconButton size="small" onClick={() => handleSaveClick(id)}>
													<CheckIcon color="primary" />
												</IconButton>
											) : (
												<IconButton size="small" onClick={() => handleEditClick(id)}>
													<EditOutlined color="primary" />
												</IconButton>
											)}

											{coverage.isMandatory && <LockOutlined color="disabled" />}
										</Stack>
									)}
								</Paper>
							</Grid>
						);
					})}
				</Grid>
			)}
		</Paper>
	);
};
